import type { BetMeter } from './socialPanelLogic'

/**
 * Round logic for the NON-crash games.
 *
 * Crash keeps `socialPanelLogic` untouched — this module exists so the shared
 * panel can serve marble and counting games without a single conditional
 * landing in crash's path.
 *
 * The difference that drives everything here: those games have no mid-round
 * cashout, so nothing can be won until the round settles. The meter therefore
 * shows what is AT STAKE while the round runs and flips to total WON on
 * settlement — whereas crash's hero is always money won, because players cash
 * out live and that caption is true at every moment.
 *
 * Pure: no React, no store, no i18n, mirroring socialPanelLogic's discipline.
 */

/** Coarse round stage, derived from the latest race event. */
export type SocialRoundStage = 'betting' | 'racing' | 'resolved' | 'settled'

/** What the meter's hero number currently means. */
export type HeroKind = 'staked' | 'won'

export interface RoundFigures {
  count: number
  winnersCount: number
  wonTotal: number
  stakedTotal: number
}

export interface MeterReadout {
  meter: BetMeter
  heroKind: HeroKind
  /** Marble rounds have no incremental progress to show until they settle. */
  showProgress: boolean
}

/**
 * THE load-bearing difference from `resolveCrashRoundPhase`: `race_ended` is a
 * normal finish for a marble round, not a bust. Crash maps it to 'crashed'.
 *
 * The default is deliberately the safe stage-setter, so an unknown or
 * pre-round event opens the panel rather than hiding it.
 */
export const resolveMarbleRoundStage = (
  latestEventType: string | undefined
): SocialRoundStage => {
  switch (latestEventType) {
    case 'race_started':
      return 'racing'
    case 'race_ended':
      return 'resolved'
    case 'race_settled':
    case 'race_cancelled':
      return 'settled'
    default:
      return 'betting'
  }
}

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n))

const wonReadout = (current: RoundFigures, mode: 'wins' | 'frozen' = 'frozen'): MeterReadout => ({
  // Field-for-field what crash's frozenMeterFor produces — pinned by a parity
  // test so the two cannot drift.
  meter: {
    mode,
    heroValue: current.wonTotal,
    cashedCount: current.winnersCount,
    totalCount: current.count,
    progress: current.count > 0 ? clamp01(current.winnersCount / current.count) : 0
  },
  heroKind: 'won',
  showProgress: true
})

/**
 * The current round's meter.
 *
 * `resolved` — the ~2s window between `race_ended` and `race_settled` — stays
 * on STAKE deliberately. Per-bet payouts only arrive after the operator's
 * RegisterWin round-trip, so flipping the caption at `race_ended` would show a
 * confident "$0.00 Total won" for a couple of seconds. The cost is that the
 * caption still reads "Total" for a moment after the marbles visibly finish,
 * which is the quieter of the two wrong states.
 */
export const resolveMarbleMeter = (input: {
  stage: SocialRoundStage
  current: RoundFigures
  /**
   * B41 — this round's wins are already known, ball by ball, from what the
   * board is drawing (Birdie only). The caution above is about payouts that
   * exist only after the operator round-trip; a Birdie win is decided by the
   * putts themselves, so the won story can run live. Off everywhere else, and
   * off is byte-for-byte the behaviour this function had before.
   */
  liveWins?: boolean
}): MeterReadout => {
  if (input.stage === 'settled') return wonReadout(input.current)
  // Live, not frozen: the round is still moving, and an idle track is what
  // says nothing has landed yet rather than that nothing will.
  if (input.liveWins === true) return wonReadout(input.current, 'wins')

  return {
    meter: {
      mode: 'wins',
      heroValue: input.current.stakedTotal,
      cashedCount: input.current.winnersCount,
      totalCount: input.current.count,
      // No progress mid-round: nothing is won yet, so a creeping fill would
      // assert something untrue.
      progress: 0
    },
    heroKind: 'staked',
    showProgress: false
  }
}

/** The Previous tab: always a finished round, so always the won story. */
export const frozenMarbleMeter = (current: RoundFigures): MeterReadout => wonReadout(current)

/**
 * THE CAPTION INVARIANT. The caption is derived from the same readout the hero
 * value came from, so it can never describe a different number than the one on
 * screen.
 *
 * Returns the ENGLISH string rather than an opaque id, because the extractor
 * only sees keys written literally inside a translate call — the view keeps
 * both literals at its own call site so they stay extractable. (Writing an
 * example translate call in this comment is what previously leaked a junk key
 * into all 16 locale files, so: don't.)
 */
export const meterCaptionKey = (heroKind: HeroKind): 'Total' | 'Total won' =>
  heroKind === 'won' ? 'Total won' : 'Total'
